import React, { useState } from 'react';
import api from './api'; // Use centralized API service

export const sendMessage = async (message) => {
  try {
    const response = await api.post('/chat', { query: message });
    return response; // New API format - processApiResponse returns data directly
  } catch (error) {
    console.error('Chat error:', error);
    throw error;
  }
};


// The longest page path the backend takes (ChatMessage.page).
const PAGE_MAX_CHARS = 300;

/**
 * Send a chat message and yield the reply's NDJSON lines. The chat is one conversation, which
 * the server picks. `page` is the path of the page the message is about (the one the user is on,
 * or the one they came from to the full chat page): KeyTa reads which property "this" means
 * from it, for this message only.
 */
export const sendMessageStreaming = async function* (message, source = null, source_event = null, talk_mode = false, { page = null } = {}) {
  console.log('🎤 sendMessageStreaming called with:', {
    message: message ? message.substring(0, 50) : null,
    source,
    source_event,
    talk_mode,
  });

  try {
    // For streaming, we need to use the raw authRequest from api.js
    const requestBody = {
      query: message,
      source: source,
      event: source_event,
      talk_mode: talk_mode
    };
    if (typeof page === 'string' && page) {
      requestBody.page = page.slice(0, PAGE_MAX_CHARS);
    }

    console.log('🎤 Request body being sent to /chat:', requestBody);
    
    const response = await api.authRequest('/chat', {
      method: 'POST',
      headers: {
        'Accept': 'application/x-ndjson'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('sendMessageStreaming: Unauthorized response, token may be invalid');
      }
      const err = new Error(`HTTP error! status: ${response.status}`);
      err.status = response.status;
      if (response.status === 402) {
        try { err.detail = await response.json(); } catch (_) {}
      }
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        // Append new data to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);

          if (line) {
            try {
              const parsed = JSON.parse(line);
              yield parsed;
            } catch (err) {
              console.error('Stream chunk parsing error:', err, 'Line:', line.substring(0, 100) + '...');
            }
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer.trim());
          yield parsed;
        } catch (err) {
          console.error('Final buffer parsing error:', err, 'Buffer:', buffer.substring(0, 100) + '...');
        }
      }
    } finally {
      // The caller stopped reading early (another account's chat is on screen now): the rest of
      // the answer is not downloaded either.
      try {
        const cancelled = reader.cancel?.();
        cancelled?.catch?.(() => {});
      } catch (_) { /* the stream is already closed */ }
    }
  } catch (error) {
    console.error('Chat error:', error);
    throw error;
  }
};


export const getChatHistory = async (options = {}) => {
  try {
    // Use centralized API service that handles both old and new response formats
    const response = await api.get('/chat', options);
    return response; // api.js processApiResponse handles format conversion
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error; // Re-throw abort error to be handled by caller
    }
    console.error('Chat history error:', error);
    throw error;
  }
};

/**
 * Send MLS form assistance request directly to My Property endpoint
 * Bypasses supervisor agent for faster, specialized MLS form help
 */
export const sendMLSFormAssistance = async (query, propertyContext = null, fieldName = null, currentValue = null) => {
  try {
    const requestData = {
      query: query,
      property_context: propertyContext,
      field_name: fieldName,
      current_value: currentValue
    };

    // Use centralized API service
    const response = await api.post('/my-property/form-assistance', requestData);
    return response; // api.js processApiResponse handles format conversion
  } catch (error) {
    console.error('MLS form assistance error:', error);
    throw error;
  }
};

/**
 * Upload document temporarily for chat processing
 */
export const uploadDocumentTemporarily = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Use api.js authRequest for file uploads (need special headers handling)
    // Explicitly set Content-Type to null so api.js allows browser to set multipart/form-data boundary
    const response = await api.authRequest('/chat/upload-document-temporarily', {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': null 
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const detail = errorData.detail;
      let errorMessage = 'Upload failed';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Handle FastAPI validation errors
        errorMessage = detail.map(d => `${d.loc.join('.')}: ${d.msg}`).join(', ');
      } else if (typeof detail === 'object') {
        errorMessage = JSON.stringify(detail);
      }
      
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Document upload error:', error);
    throw error;
  }
};

/**
 * Delete temporarily uploaded document
 */
export const deleteTemporaryDocument = async (filePath) => {
  try {
    const response = await api.delete(`/chat/delete-temporarily-document?file_path=${encodeURIComponent(filePath)}`);
    return response; // New API format - processApiResponse returns data directly
  } catch (error) {
    console.error('Document delete error:', error);
    throw error;
  }
};


export const submitChatFeedback = async ({ thread_id, message_id, feedback_value, reason_code = null, comment = null }) => {
  try {
    const response = await api.post('/chat/feedback', {
      thread_id,
      message_id,
      feedback_value,
      reason_code,
      comment,
    });
    return response;
  } catch (error) {
    console.error('Chat feedback error:', error);
    throw error;
  }
};

// ---- confirm cards (routers/actions.py) ------------------------------------------------------

/**
 * Run an action the chat asked the user to confirm. The backend adds the outcome to the chat as
 * an assistant line carrying pending_outcome { pending_id, action, status }, and answers
 * { kind, action, output, thread_id, outcome, property_label }: the chat's thread, the line's
 * text (null when none was added) and the property's short address.
 */
export const confirmPendingAction = (pendingId) =>
  api.post(`/actions/pending/${encodeURIComponent(pendingId)}/confirm`, {});

/** Drop an action the chat asked the user to confirm; nothing runs. */
export const cancelPendingAction = (pendingId) =>
  api.post(`/actions/pending/${encodeURIComponent(pendingId)}/cancel`, {});

// One look at the open confirmations serves every card rendered at about the same time.
const OPEN_PENDING_TTL_MS = 10 * 1000;
let openPendingRequest = null;
let openPendingAt = 0;

/** The ids of the confirmations still waiting for this user, as a Set; never redirects on 401. */
export const listOpenPendingIds = () => {
  const now = Date.now();
  if (!openPendingRequest || now - openPendingAt > OPEN_PENDING_TTL_MS) {
    openPendingAt = now;
    openPendingRequest = api.get('/actions/pending', { skipAuthHandling: true })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : rows?.data;
        return new Set((Array.isArray(list) ? list : []).map((row) => row?.id).filter(Boolean));
      })
      .catch((error) => {
        openPendingRequest = null;
        throw error;
      });
  }
  return openPendingRequest;
};

/** Forget the cached open confirmations (after confirming or cancelling one). */
export const forgetOpenPending = () => {
  openPendingRequest = null;
};
